import { InjectionToken, Provider, Type } from '@angular/core';
import {
  ANNUITY_CHART_PRESETS,
  AnnuityChartComponent,
  BarChartExplainComponent,
  BoxPlotExplainComponent,
  EchartsChartComponent,
  FAIR_GAME_SIM_PRESETS,
  FairGameSimComponent,
  HistogramExplainComponent,
  INV_NORM_LAB_PRESETS,
  InvNormLabComponent,
  LINEAR_TRANSFORM_PLAYGROUND_PRESETS,
  LinearTransformPlaygroundComponent,
  NormalDistributionChartComponent,
  NUMBER_SETS_VENN_PRESETS,
  NumberSetsVennComponent,
  OgiveExplainComponent,
  PASCALS_TRIANGLE_PRESETS,
  PascalsTriangleComponent,
  PascalsTriangleExplainComponent,
  PDF_EXPLORER_PRESETS,
  PdfExplorerComponent,
  PIN_CODE_PRESETS,
  PinCodeWidgetComponent,
  POISSON_EXPLORER_PRESETS,
  PoissonExplorerComponent,
  POPULATION_PRESETS,
  PopulationWidgetComponent,
  PROBABILITY_PRESETS,
  ProbabilityComponent,
  ROUNDING_DP_PRESETS,
  RoundingDpComponent,
  ROUNDING_SF_PRESETS,
  RoundingSfComponent,
  ScatterPlotExplainComponent,
  SEQUENCE_SERIES_PRESETS,
  SequenceSeriesComponent,
  UPPER_LOWER_BOUNDS_PRESETS,
  UpperLowerBoundsComponent,
  URN_PRESETS,
  UrnComponent,
  VENN_DASHBOARD_PRESETS,
  VennDashboardComponent,
  VENN_DIAGRAM_PRESETS,
  VennDiagramComponent,
  Z_LAB_PRESETS,
  ZLabComponent,
} from '@examdojo/math-widget';
import merge from 'lodash/merge';

export interface CustomComponentRegistryEntry {
  component: Type<unknown>;
  presets?: Record<string, Record<string, unknown>>;
}

export type CustomComponentRegistry = Map<string, CustomComponentRegistryEntry>;

export const CUSTOM_COMPONENT_REGISTRY = new InjectionToken<CustomComponentRegistry>('CUSTOM_COMPONENT_REGISTRY');

export const DEFAULT_CUSTOM_COMPONENTS: CustomComponentRegistry = new Map<string, CustomComponentRegistryEntry>([
  [
    'sequence-series-chart',
    {
      component: SequenceSeriesComponent,
      presets: SEQUENCE_SERIES_PRESETS,
    },
  ],
  [
    'probability',
    {
      component: ProbabilityComponent,
      presets: PROBABILITY_PRESETS,
    },
  ],
  [
    'bar-chart-explain',
    {
      component: BarChartExplainComponent,
    },
  ],
  [
    'echarts-chart',
    {
      component: EchartsChartComponent,
    },
  ],
  [
    'box-plot-explain',
    {
      component: BoxPlotExplainComponent,
    },
  ],
  [
    'ogive-chart',
    {
      component: OgiveExplainComponent,
    },
  ],
  [
    'histogram-explain',
    {
      component: HistogramExplainComponent,
    },
  ],
  [
    'scatter-plot-explain',
    {
      component: ScatterPlotExplainComponent,
    },
  ],
  [
    'number-sets-venn',
    {
      component: NumberSetsVennComponent,
      presets: NUMBER_SETS_VENN_PRESETS,
    },
  ],
  [
    'population',
    {
      component: PopulationWidgetComponent,
      presets: POPULATION_PRESETS,
    },
  ],
  [
    'normal-distribution-chart',
    {
      component: NormalDistributionChartComponent,
    },
  ],
  [
    'annuity-chart',
    {
      component: AnnuityChartComponent,
      presets: ANNUITY_CHART_PRESETS,
    },
  ],
  [
    'rounding-dp',
    {
      component: RoundingDpComponent,
      presets: ROUNDING_DP_PRESETS,
    },
  ],
  [
    'rounding-sf',
    {
      component: RoundingSfComponent,
      presets: ROUNDING_SF_PRESETS,
    },
  ],
  [
    'upper-lower-bounds',
    {
      component: UpperLowerBoundsComponent,
      presets: UPPER_LOWER_BOUNDS_PRESETS,
    },
  ],
  [
    'venn-diagram',
    {
      component: VennDiagramComponent,
      presets: VENN_DIAGRAM_PRESETS,
    },
  ],
  [
    'venn-dashboard',
    {
      component: VennDashboardComponent,
      presets: VENN_DASHBOARD_PRESETS,
    },
  ],
  [
    'fair-game-sim',
    {
      component: FairGameSimComponent,
      presets: FAIR_GAME_SIM_PRESETS,
    },
  ],
  [
    'inv-norm-lab',
    {
      component: InvNormLabComponent,
      presets: INV_NORM_LAB_PRESETS,
    },
  ],
  [
    'z-lab',
    {
      component: ZLabComponent,
      presets: Z_LAB_PRESETS,
    },
  ],
  [
    'pin-code',
    {
      component: PinCodeWidgetComponent,
      presets: PIN_CODE_PRESETS,
    },
  ],
  [
    'urn',
    {
      component: UrnComponent,
      presets: URN_PRESETS,
    },
  ],
  [
    'pascals-triangle',
    {
      component: PascalsTriangleComponent,
      presets: PASCALS_TRIANGLE_PRESETS,
    },
  ],
  [
    'pascals-triangle-explain',
    {
      component: PascalsTriangleExplainComponent,
      presets: PASCALS_TRIANGLE_PRESETS,
    },
  ],
  [
    'pdf-explorer',
    {
      component: PdfExplorerComponent,
      presets: PDF_EXPLORER_PRESETS,
    },
  ],
  [
    'poisson-explorer',
    {
      component: PoissonExplorerComponent,
      presets: POISSON_EXPLORER_PRESETS,
    },
  ],
  [
    'linear-transform-playground',
    {
      component: LinearTransformPlaygroundComponent,
      presets: LINEAR_TRANSFORM_PLAYGROUND_PRESETS,
    },
  ],
]);

export function provideCustomComponentRegistry(
  registry: CustomComponentRegistry = DEFAULT_CUSTOM_COMPONENTS,
): Provider {
  return { provide: CUSTOM_COMPONENT_REGISTRY, useValue: registry };
}

/**
 * Resolves a preset + overrides into a final inputs object.
 * If a preset is specified, deep-merges it with the inputs (overrides).
 * If no preset, returns inputs as-is.
 */
export function resolveComponentInputs(
  entry: CustomComponentRegistryEntry,
  preset: string | undefined,
  inputs: Record<string, unknown>,
): Record<string, unknown> {
  if (!preset || !entry.presets?.[preset]) {
    return inputs;
  }

  const base = structuredClone(entry.presets[preset]);
  return merge(base, inputs);
}
